<template>
  <div>
    <invite-alert
      v-if="alert.show"
      :alert="alert"
      @cancel="$emit('resetAlert')"
    />

    <fieldset :disabled="loadingCreation || createdInvite">
      <form
        novalidate="true"
        action="javascript:void(0)"
        autocomplete="off"
        @submit="createInvite"
      >
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <emd-text
                    tag="p"
                    size="medium"
                    type="caption"
                    >{{ $t('invite.create.user-data') }}</emd-text
                  >
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <div class="emd-form-group">
                    <label
                      class="emd-label _required"
                      for="firstName"
                      >{{ $t('invite.create.first-name') }}</label
                    >
                    <input
                      class="emd-input"
                      type="text"
                      id="firstName"
                      name="firstName"
                      v-model="user.firstName"
                      @input="validate"
                    />
                  </div>
                </div>
                <div class="el-chunk">
                  <div class="emd-form-group">
                    <label
                      class="emd-label _required"
                      for="lastName"
                      >{{ $t('invite.create.last-name') }}</label
                    >
                    <input
                      class="emd-input"
                      type="text"
                      id="lastName"
                      name="lastName"
                      v-model="user.lastName"
                      @input="validate"
                    />
                  </div>
                </div>
                <div class="el-chunk">
                  <div class="emd-form-group">
                    <label
                      class="emd-label _required"
                      for="email"
                      >{{ $t('invite.create.email') }}</label
                    >
                    <input
                      class="emd-input"
                      type="text"
                      name="email"
                      id="email"
                      v-model="user.email"
                      @input="validate"
                      :class="{
                        _state_error: user.email && $v.user.email.$invalid
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <emd-text
                    tag="p"
                    size="medium"
                    type="caption"
                    >{{ $t('invite.create.permission') }}
                  </emd-text>
                </div>
              </div>
              <div class="emd-panel__block">
                <role-form-group
                  :userRole="user.role"
                  @update:model-value="newValue => (user.role = newValue)"
                />
              </div>
            </div>
          </div>
          <hr class="emd-divider eu-my_4" />
          <password-confirmation
            v-model="password"
            label="invite.create.password-label"
          ></password-confirmation>
        </div>
        <div
          class="emd-container__footer"
          v-if="!createdInvite"
        >
          <router-link
            tag="button"
            class="emd-btn _color_primary _type_text"
            type="button"
            :to="{ name: 'invites' }"
            >{{ $t('invite.create.button-cancel') }}
          </router-link>
          <emd-button
            color="primary"
            type-html="submit"
            :disabled="$v.user.$invalid || !password"
          >
            <emd-loader v-if="loadingCreation"></emd-loader>
            <template v-else>{{ $t('invite.create.button-ok') }}</template>
          </emd-button>
        </div>
      </form>
    </fieldset>
  </div>
</template>
<script>
import { EmdLoader, EmdText, EmdButton } from 'emerald-vue/src/components'
import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'
import { required, email } from 'vuelidate/lib/validators'
import { formatErrorMessage } from '@/support/formatErrorMessage'
import { UtilitiesInstance } from '@/store'
import { mapActions, mapGetters } from 'vuex'
import InviteAlert from '@/views/Team/Invites/components/InviteAlert.vue'
import RoleFormGroup from '@/components/project/ui/RoleFormGroup/Index.vue'

export default {
  name: 'FormInviteCreate',
  components: {
    InviteAlert,
    EmdLoader,
    PasswordConfirmation,
    RoleFormGroup,
    EmdText,
    EmdButton
  },
  data () {
    return {
      loadingCreation: false,
      password: '',
      createdInvite: false,
      user: {
        firstName: null,
        lastName: null,
        email: null,
        role: null
      },

      alert: {
        show: false,
        isError: false,
        messages: []
      }
    }
  },
  validations: {
    password: { required },
    user: {
      firstName: { required },
      lastName: { required },
      email: {
        required,
        email
      },
      role: { required }
    }
  },
  computed: {
    ...mapGetters(['accountId', 'merchantId'])
  },
  methods: {
    ...mapActions(['CREATE_INVITE']),

    showAlert (messages, isError = true) {
      this.alert = {
        show: true,
        isError: isError,
        messages: messages
      }
    },

    resetAlert () {
      this.alert = {
        show: false,
        isError: false,
        messages: []
      }
    },

    payloadBody () {
      return {
        merchant: {
          id: this.merchantId,
          type: 'Staff',
          account: {
            id: this.accountId,
            role: this.user.role
          }
        },
        user: {
          email: this.user.email,
          first_name: this.user.firstName,
          last_name: this.user.lastName
        },
        password: window.btoa(this.password)
      }
    },

    async createInvite () {
      try {
        this.resetAlert()

        if (this.$v.user.$invalid || this.$v.password.$invalid) {
          this.showAlert([this.$t('invite.list.error-required-fields')])
          this.createdInvite = false
          return
        }

        this.loadingCreation = true

        await this.CREATE_INVITE({ body: this.payloadBody() })

        this.loadingCreation = false
        this.createdInvite = true
        this.showAlert([this.$t('invite.create.success-invite')], false)

        window.scrollTo(0, 0)
        setTimeout(() => {
          this.$router
            .push({
              name: 'invites'
            })
            .catch(err => err)
        }, 7000)
      } catch (err) {
        this.loadingCreation = false
        this.createdInvite = false
        this.showAlert(
          formatErrorMessage(err, this.$t('invite.list.error-internal'))
        )
        window.scrollTo(0, 0)
      }
    },
    validate (e) {
      if (e.target.name === 'email') {
        this.user[e.target.name] = UtilitiesInstance.commons
          .removeSpecialCharacters(e.target.value, 'email')
          .toLowerCase()
      } else {
        this.user[e.target.name] =
          UtilitiesInstance.commons.removeSpecialCharacters(e.target.value)
      }
    }
  }
}
</script>
